import { api } from '../api/client';
import { clearSession, session } from './session';

/**
 * 应用启动时的会话初始化。
 *
 * ★ 安全关键：微信授权回调把**一次性票据**放在 fragment 里带回来。
 * 拿到后必须立刻做两件事：换成 token，然后把票据从地址栏抹掉。
 *
 * 不抹掉的话，医生把地址栏链接转发出去，链接里就带着可用的票据。
 * （长期 token 从来不进 URL，那是更严重的问题，后端已经不这么做了。）
 */
export async function bootstrapSession(): Promise<void> {
  const ticket = readTicketFromHash();

  if (ticket) {
    stripTicketFromUrl();
    try {
      await api.exchangeTicket(ticket);
    } catch {
      // 票据过期或已被用过 —— 不弹错，让路由守卫把人送去登录
      clearSession();
    }
  }

  // 业务员用的是 admin token，/me 只认医生 —— 别拿它去撞 403
  if (session.token && session.kind !== 'sales') {
    try {
      const { doctor, points } = await api.me();
      session.doctor = doctor;
      session.points = points;
    } catch {
      // 401 时 client 已经清过会话了，这里不用再处理
    }
  }

  session.ready = true;
}

function readTicketFromHash(): string {
  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  if (!hash.includes('ticket=')) return '';
  try {
    return new URLSearchParams(hash).get('ticket') || '';
  } catch {
    return '';
  }
}

/** 用 replaceState 抹掉票据，同时不留下一条可以后退回去的历史记录 */
function stripTicketFromUrl(): void {
  try {
    history.replaceState(null, '', location.pathname + location.search);
  } catch {
    /* 极端环境下失败也不阻断登录 */
  }
}

/** 刷新余额。扫卡、兑换之后调用，让首页数字立刻跟上。 */
export async function refreshPoints(): Promise<void> {
  try {
    session.points = await api.balance();
  } catch {
    /* 静默失败，下次进首页会重新拉 */
  }
}

/** 刷新医生资料 + 余额。资料页提交后调用，其它页面的展示立即跟上。 */
export async function refreshProfile(): Promise<void> {
  try {
    const { doctor, points } = await api.me();
    session.doctor = doctor;
    session.points = points;
  } catch {
    /* 静默失败，下次启动会重新拉 */
  }
}
